import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createEditor, Editor, Element as SlateElement, Transforms } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { Box, Divider, IconButton, Stack, Tooltip, Typography, CircularProgress } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { resolveSupportAssetUrl } from '../utils/supportAssets';

export const createEmptyArticleContent = () => [
    {
        type: 'paragraph',
        children: [{ text: '' }],
    },
];

export const EMPTY_ARTICLE_CONTENT = createEmptyArticleContent();

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

const withImages = (editor) => {
    const { isVoid } = editor;
    editor.isVoid = (element) => (element.type === 'image' ? true : isVoid(element));
    return editor;
};

const ToolbarButton = ({ label, icon, onClick, active, disabled }) => (
    <Tooltip title={label}>
        <span>
            <IconButton
                size="small"
                onMouseDown={(event) => {
                    event.preventDefault();
                    if (!disabled) onClick();
                }}
                color={active ? 'primary' : 'default'}
                disabled={disabled}
                sx={{ borderRadius: 1 }}
            >
                {icon}
            </IconButton>
        </span>
    </Tooltip>
);

const safeDoc = (val) =>
    Array.isArray(val) && val.length ? val : createEmptyArticleContent();

const SupportArticleEditor = ({ value, onChange, onUploadImage, readOnly = false }) => {
    const editor = useMemo(() => withImages(withReact(createEditor())), []);
    const doc = useMemo(() => safeDoc(value), [value]);

    // Bump a key whenever the external doc changes so Slate re-reads initialValue on mount.
    const [slateKey, setSlateKey] = useState(0);
    useEffect(() => {
        setSlateKey((k) => k + 1);
    }, [doc]);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleChange = (nextValue) => {
        if (onChange) onChange(nextValue);
    };

    const isMarkActive = (format) => {
        const marks = Editor.marks(editor);
        return marks ? marks[format] === true : false;
    };

    const toggleMark = (format) => {
        const isActive = isMarkActive(format);
        if (isActive) {
            Editor.removeMark(editor, format);
        } else {
            Editor.addMark(editor, format, true);
        }
    };

    const isBlockActive = (format) => {
        if (!editor.selection) return false;
        const [match] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
        });
        return !!match;
    };

    const toggleBlock = (format) => {
        const isActive = isBlockActive(format);
        const isList = LIST_TYPES.includes(format);

        Transforms.unwrapNodes(editor, {
            match: (n) =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                LIST_TYPES.includes(n.type) &&
                n.type !== format,
            split: true,
        });

        let newType = 'paragraph';
        if (!isActive) {
            newType = isList ? 'list-item' : format;
        }
        Transforms.setNodes(editor, { type: newType });
        if (!isActive && isList) {
            const block = { type: format, children: [] };
            Transforms.wrapNodes(editor, block);
        }
    };

    const insertImage = useCallback(
        (url) => {
            const image = {
                type: 'image',
                url,
                children: [{ text: '' }],
            };
            Transforms.insertNodes(editor, image);
        },
        [editor]
    );

    const handleImageUpload = useCallback(() => {
        if (!onUploadImage || readOnly) return;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async () => {
            const file = input.files && input.files[0];
            if (!file) return;
            try {
                setUploadError('');
                setUploading(true);
                const url = await onUploadImage(file);
                if (url) insertImage(url);
            } catch (error) {
                const message = typeof error === 'string' ? error : error?.message || 'Failed to upload image.';
                setUploadError(message);
            } finally {
                setUploading(false);
            }
        };
        input.click();
    }, [insertImage, onUploadImage, readOnly]);

    const renderElement = useCallback((props) => {
        const { element, attributes, children } = props;
        switch (element.type) {
            case 'heading-one':
                return (
                    <h1 {...attributes} style={{ fontSize: '1.8rem', margin: '16px 0 8px' }}>
                        {children}
                    </h1>
                );
            case 'heading-two':
                return (
                    <h2 {...attributes} style={{ fontSize: '1.4rem', margin: '16px 0 8px' }}>
                        {children}
                    </h2>
                );
            case 'block-quote':
                return (
                    <blockquote
                        {...attributes}
                        style={{
                            borderLeft: '4px solid #90caf9',
                            margin: '16px 0',
                            padding: '8px 16px',
                            color: '#555',
                            fontStyle: 'italic',
                        }}
                    >
                        {children}
                    </blockquote>
                );
            case 'bulleted-list':
                return (
                    <ul {...attributes} style={{ margin: '8px 0 8px 24px' }}>
                        {children}
                    </ul>
                );
            case 'numbered-list':
                return (
                    <ol {...attributes} style={{ margin: '8px 0 8px 24px' }}>
                        {children}
                    </ol>
                );
            case 'list-item':
                return <li {...attributes}>{children}</li>;
            case 'code':
                return (
                    <pre
                        {...attributes}
                        style={{
                            background: '#f5f5f5',
                            padding: '12px',
                            borderRadius: 4,
                            fontSize: '0.9rem',
                            overflowX: 'auto',
                        }}
                    >
                        <code>{children}</code>
                    </pre>
                );
            case 'image': {
                const removeImage = () => {
                    const path = ReactEditor.findPath(editor, element);
                    Transforms.removeNodes(editor, { at: path });
                };

                const handleImageClick = (event) => {
                    if (readOnly) return;
                    event.preventDefault();
                    removeImage();
                };

                return (
                    <div {...attributes} style={{ textAlign: 'center', margin: '16px 0' }}>
                        <div contentEditable={false} style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                                src={resolveSupportAssetUrl(element.url)}
                                alt="Support article visual"
                                style={{ maxWidth: '100%', borderRadius: 8, cursor: readOnly ? 'default' : 'pointer' }}
                                onMouseDown={(event) => {
                                    if (readOnly) return;
                                    event.preventDefault();
                                }}
                                onClick={handleImageClick}
                            />
                            {!readOnly && (
                                <IconButton
                                    size="small"
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        removeImage();
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        backgroundColor: 'rgba(33, 33, 33, 0.6)',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: 'rgba(33, 33, 33, 0.8)',
                                        },
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                        {children}
                    </div>
                );
            }
            default:
                return (
                    <p {...attributes} style={{ margin: '8px 0', lineHeight: 1.6 }}>
                        {children}
                    </p>
                );
        }
    }, [editor, readOnly]);

    const renderLeaf = useCallback((props) => {
        const { attributes, children, leaf } = props;
        let style = {};
        if (leaf.bold) {
            style.fontWeight = 'bold';
        }
        if (leaf.italic) {
            style.fontStyle = 'italic';
        }
        if (leaf.underline) {
            style.textDecoration = style.textDecoration ? `${style.textDecoration} underline` : 'underline';
        }
        if (leaf.code) {
            style = {
                ...style,
                backgroundColor: '#f5f5f5',
                borderRadius: 4,
                fontFamily: 'monospace',
                padding: '0 4px',
            };
        }
        return (
            <span {...attributes} style={style}>
                {children}
            </span>
        );
    }, []);

    return (
        <Box>
            {!readOnly && (
                <Box sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <ToolbarButton label="Heading 1" icon={<LooksOneIcon fontSize="small" />} onClick={() => toggleBlock('heading-one')} active={isBlockActive('heading-one')} />
                        <ToolbarButton label="Heading 2" icon={<LooksTwoIcon fontSize="small" />} onClick={() => toggleBlock('heading-two')} active={isBlockActive('heading-two')} />
                        <ToolbarButton label="Bold" icon={<FormatBoldIcon fontSize="small" />} onClick={() => toggleMark('bold')} active={isMarkActive('bold')} />
                        <ToolbarButton label="Italic" icon={<FormatItalicIcon fontSize="small" />} onClick={() => toggleMark('italic')} active={isMarkActive('italic')} />
                        <ToolbarButton label="Underline" icon={<FormatUnderlinedIcon fontSize="small" />} onClick={() => toggleMark('underline')} active={isMarkActive('underline')} />
                        <ToolbarButton label="Code" icon={<CodeIcon fontSize="small" />} onClick={() => toggleMark('code')} active={isMarkActive('code')} />
                        <ToolbarButton label="Block Quote" icon={<FormatQuoteIcon fontSize="small" />} onClick={() => toggleBlock('block-quote')} active={isBlockActive('block-quote')} />
                        <ToolbarButton label="Bulleted List" icon={<FormatListBulletedIcon fontSize="small" />} onClick={() => toggleBlock('bulleted-list')} active={isBlockActive('bulleted-list')} />
                        <ToolbarButton label="Numbered List" icon={<FormatListNumberedIcon fontSize="small" />} onClick={() => toggleBlock('numbered-list')} active={isBlockActive('numbered-list')} />
                        <ToolbarButton label="Insert Image" icon={<InsertPhotoIcon fontSize="small" />} onClick={handleImageUpload} active={false} disabled={uploading} />
                        {uploading && <CircularProgress size={20} />}
                    </Stack>
                    <Divider sx={{ mt: 1 }} />
                    {uploadError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            {uploadError}
                        </Typography>
                    )}
                </Box>
            )}

            <Slate
                key={slateKey}
                editor={editor}
                initialValue={doc}
                onChange={handleChange}
            >
                <Editable
                    readOnly={readOnly}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    spellCheck
                    placeholder={readOnly ? '' : 'Write your support article content here...'}
                    style={{ minHeight: 260, caretColor: '#1976d2' }}
                />
            </Slate>
        </Box>
    );
};

export default SupportArticleEditor;
